import type { TReviewLineItem } from "./types";

/**
 * Segment 4 ("Inter Company Code") holds entity codes that also live in the property
 * master, so its options are labelled with the property's name instead of the segment
 * master's own name — which the Yardi lookup import fills with a copy of the code.
 * The backend supplies those names (`property_description` / `segment_4_descriptions`).
 */
export const PROPERTY_LINKED_SEGMENT = 4;

/**
 * Segments 5 ("Movements") and 6 ("Analytical Dimensions") take their options from
 * the custom list of the account a line sits on when that account has one, and from
 * the company-wide master list otherwise. Mirrors `CUSTOM_LIST_SEGMENTS` in
 * `core/services/segments/segment_resolver.py`, which resolves the same rule server-side.
 */
export const CUSTOM_LIST_SEGMENTS = [5, 6];

/**
 * Segment 2 ("Schedule") also comes from a custom list, but one scoped to the line's
 * property code as well as its account — the only segment whose permitted values
 * depend on both. It has no master list to fall back to. Mirrors
 * `PROPERTY_SCOPED_SEGMENT` in `core/services/segments/segment_resolver.py`.
 */
export const PROPERTY_SCOPED_SEGMENT = 2;

/** Segments whose options come from a custom list rather than the master list. */
export const CUSTOM_LIST_SEGMENTS_ALL = [PROPERTY_SCOPED_SEGMENT, ...CUSTOM_LIST_SEGMENTS];

// Define fields for the edit modal
export type TLineItemFieldDef = {
    key: keyof TReviewLineItem;
    label: string;
    type: "text" | "number" | "dropdown";
    required?: boolean;
    alwaysReadOnly?: boolean;
    readOnlyWhenHasValue?: boolean;
    /**
     * Editable on split rows only. A split row's amount is the one value the
     * reviewer has to change — it starts at 0 and the row stays invalid until the
     * split adds up to the original — while amounts on ordinary rows come from the
     * source file and remain read-only.
     */
    editableOnSplitRows?: boolean;
    dropdownSource?:
        | "propcode"
        | "coaCode"
        | "coaName"
        | "segment2"
        | "segment4"
        | "segment5"
        | "segment6"
        | "segment7";
};

/**
 * Fields that are mandatory on TB/GL/OTB lines but optional on FinJournal lines.
 * FinJournals are posted as-is, so a line without a mapped Propcode or Acc No is
 * still saveable and still valid — mirrors the FinJournal exemption in
 * `_missing_mapping_errors` (`core/services/file_processing/processing_service.py`).
 */
export const FINJOURNAL_OPTIONAL_FIELD_KEYS: ReadonlyArray<keyof TReviewLineItem> = [
    "propcode",
    "breAccountNumber",
];

export const lineItemFields: TLineItemFieldDef[] = [
    { key: "trannum", label: "Trannum", type: "text", alwaysReadOnly: true },
    {
        key: "propcode",
        label: "Propcode",
        type: "dropdown",
        dropdownSource: "propcode",
        required: true,
    },
    { key: "localPropcode", label: "Local Prop Code", type: "text", readOnlyWhenHasValue: true },
    { key: "sourceAccountNumber", label: "Source Acc No", type: "text", alwaysReadOnly: true },
    { key: "sourceAccountName", label: "Source Acc Name", type: "text", alwaysReadOnly: true },
    {
        key: "breAccountNumber",
        label: "Acc No",
        type: "dropdown",
        dropdownSource: "coaCode",
        required: true,
    },
    { key: "breAccountName", label: "Acc Name", type: "dropdown", dropdownSource: "coaName" },
    { key: "amount", label: "Amount", type: "text", editableOnSplitRows: true },
    { key: "debit", label: "Debit", type: "text", alwaysReadOnly: true },
    { key: "credit", label: "Credit", type: "text", alwaysReadOnly: true },
    { key: "transactionDate", label: "Date", type: "text", alwaysReadOnly: true },
    { key: "postMonth", label: "Post Month", type: "text", alwaysReadOnly: true },
    { key: "bookNumber", label: "Book Number", type: "number", alwaysReadOnly: true },
    { key: "description", label: "Detail Notes", type: "text" },
    { key: "ref", label: "Ref", type: "text", alwaysReadOnly: true },
    { key: "segment1", label: "Segment 1 (Recoverability)", type: "text" },
    {
        key: "mappedSegment2",
        label: "Segment 2 (Schedule)",
        type: "dropdown",
        dropdownSource: "segment2",
    },
    { key: "segment3", label: "Segment 3 (Tenant)", type: "text" },
    {
        key: "mappedSegment4",
        label: "Segment 4 (Inter Company Code)",
        type: "dropdown",
        dropdownSource: "segment4",
    },
    {
        key: "mappedSegment5",
        label: "Segment 5 (Movements)",
        type: "dropdown",
        dropdownSource: "segment5",
    },
    {
        key: "mappedSegment6",
        label: "Segment 6 (Analytical Dimensions)",
        type: "dropdown",
        dropdownSource: "segment6",
    },
    {
        key: "mappedSegment7",
        label: "Segment 7 (Deal)",
        type: "dropdown",
        dropdownSource: "segment7",
    },
    { key: "exchangeRate", label: "Exchange Rate", type: "text" },
    { key: "exchangeRateDate", label: "Exchange Rate Date", type: "text" },
    { key: "displayType", label: "Display Type", type: "text" },
    { key: "localCurrency", label: "Local Currency", type: "text" },
    { key: "localAmount", label: "Local Amount", type: "text" },
];

/**
 * Edit-modal field definitions for a file of `fileType`.
 *
 * Identical to `lineItemFields` except on FinJournals, where Propcode and Acc No
 * carry no required flag — so the modal shows no asterisk and the save is not
 * blocked when they are blank.
 */
export function resolveLineItemFields(fileType: string | undefined): TLineItemFieldDef[] {
    if (fileType !== "financial_journals") return lineItemFields;
    return lineItemFields.map((field) =>
        FINJOURNAL_OPTIONAL_FIELD_KEYS.includes(field.key) ? { ...field, required: false } : field,
    );
}
